<script setup>
import { ref, computed, inject, onMounted, watch } from 'vue';
import * as navConfig from '@/navConfig';
import TireVaultLogo from '@/components/Brand/TireVaultLogo';
import { router, usePage } from '@inertiajs/vue3';
import { useDisplay } from 'vuetify';
import { useUserStore } from '@/stores/user';
import { useOptionsStore } from '@/stores/options';
import { useDashboardStore } from '@/stores/dashboard';
import { storeToRefs } from 'pinia';
import { isRole } from '@/utils/roleHelper';
import { loadLanguageAsync } from 'laravel-vue-i18n';

defineOptions({
    inheritAttrs: false
});

const drawer = ref(true);
const rail = ref(false);
const { thresholds } = useDisplay();

const userStore = useUserStore();
const { profile, lastLogin, locale, dealerships, activeDealership } = storeToRefs(userStore);
const { securityCheckpoint } = storeToRefs(useOptionsStore());
const { pulse } = storeToRefs(useDashboardStore());
const roles = computed(() => isRole(profile.value));

const globalSearch = ref('');

const runGlobalSearch = () => {
    const term = globalSearch.value.trim();
    if (!term) {
        return;
    }
    globalSearch.value = '';
    router.visit('/tire-sets?term=' + encodeURIComponent(term));
};

const startCheckIn = () => {
    router.visit('/tire-sets?check_in=1');
};

const capacityPercent = computed(() => {
    if (!pulse.value?.capacity) {
        return 0;
    }
    return Math.min(100, Math.round((pulse.value.stored / pulse.value.capacity) * 100));
});

const navBadge = (route) => {
    if (route.path === '/requests' && pulse.value?.open_requests) {
        return pulse.value.open_requests;
    }
    return null;
};

const isLoading = inject('isLoading');

const routes = computed(() => {
    return activeDealership.value
        ? navConfig.dealership(roles.value)
        : navConfig.platform(roles.value);
});

const toolRoutes = computed(() => {
    return roles.value.superAdmin() ? navConfig.bottomNavRoutes() : [];
});

const showProjectSetup = computed(() => {
    return import.meta.env.VITE_ENV === 'local' && roles.value.superAdmin();
});

const initials = computed(() => {
    if (!profile.value) {
        return '';
    }
    return `${profile.value.first_name?.[0] ?? ''}${profile.value.last_name?.[0] ?? ''}`.toUpperCase();
});

const roleLabel = computed(() => userStore.getUserRoleString(profile.value) ?? '');

const pageTitle = computed(() => {
    return navConfig.allRoutes().find(route => route.name === usePage().component)?.label ?? null;
});

onMounted(() => {
    if (window.innerWidth <= thresholds.value.md) {
        drawer.value = false;
    }
    try {
        rail.value = localStorage.getItem('tv_sidebar_rail') === '1';
    } catch {
        rail.value = false;
    }
});

const toggleRail = () => {
    rail.value = !rail.value;
    try {
        localStorage.setItem('tv_sidebar_rail', rail.value ? '1' : '0');
    } catch {
        // localStorage unavailable; the preference just won't persist
    }
};

const logout = async () => {
    await router.post('/logout');
};

const switchDealership = (hash) => {
    router.post('/select-dealership', { dealership: hash });
};

const changeBar = () => {
    if (window.innerWidth <= thresholds.value.md) {
        rail.value = false;
    }
    drawer.value = !drawer.value;
};

const activeRoute = ref(null);

const visitPath = (route) => {
    activeRoute.value = route.path;
    if (route.private) {
        window.open(route.path, '_blank');
    } else {
        router.visit(route.path);
    }
    if (window.innerWidth <= thresholds.value.md) {
        drawer.value = false;
    }
};

const { setLocale } = useUserStore();
watch(locale, (lang) => {
    loadLanguageAsync(lang);
});
</script>

<template>
<div>
    <v-app>
        <v-navigation-drawer
            v-if="!securityCheckpoint"
            v-model="drawer"
            :rail="rail"
            :rail-width="68"
            :width="240"
            mobile-breakpoint="md"
            color="primary"
            class="tv-sidebar"
            app>
            <div class="flex flex-col h-full text-sm">
                <div
                    class="flex items-center px-4 py-4 border-b border-white/10 shrink-0 cursor-pointer"
                    :class="rail ? 'justify-center px-0' : ''"
                    @click="$inertia.visit('/')">
                    <TireVaultLogo
                        :size="26"
                        :wordmark="!rail"
                        class="text-lg" />
                </div>

                <div
                    v-if="activeDealership || dealerships.length"
                    class="px-2.5 py-3 border-b border-white/10 shrink-0">
                    <button
                        class="w-full flex items-center text-white/90 text-xs font-medium rounded-md px-2.5 py-2 bg-white/10 hover:bg-white/15 transition"
                        :class="rail ? 'justify-center' : 'justify-between'"
                        :title="activeDealership?.name ?? $t('label.platform')">
                        <span class="flex items-center gap-2 min-w-0">
                            <v-icon
                                icon="mdi-office-building-outline"
                                size="14"
                                class="shrink-0" />
                            <span
                                v-show="!rail"
                                class="truncate">{{ activeDealership?.name ?? $t('label.platform') }}</span>
                        </span>
                        <v-icon
                            v-show="!rail"
                            icon="mdi-chevron-down"
                            size="12"
                            class="shrink-0 ml-2" />
                        <v-menu activator="parent">
                            <v-list density="compact">
                                <v-list-item
                                    v-for="dealership in dealerships"
                                    :key="dealership.hash"
                                    :active="dealership.hash === activeDealership?.hash"
                                    @click="switchDealership(dealership.hash)">
                                    <v-list-item-title>{{ dealership.name }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    v-if="roles.superAdmin()"
                                    :active="!activeDealership"
                                    @click="switchDealership(null)">
                                    <v-list-item-title>{{ $t('label.platform') }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </button>
                </div>

                <nav class="flex-1 overflow-y-auto px-2.5 py-3 space-y-0.5">
                    <a
                        v-for="route in routes"
                        :key="route.name"
                        v-tooltip.right="{ content: $t(route.label), disabled: !rail }"
                        class="relative flex items-center gap-3 py-2 rounded-md transition cursor-pointer"
                        :class="[
                            rail ? 'justify-center px-0' : 'px-3',
                            route.name === $page.component
                                ? 'bg-white/15 text-white font-semibold'
                                : 'text-white/70 hover:text-white hover:bg-white/10'
                        ]"
                        @click="visitPath(route)">
                        <v-progress-circular
                            v-if="isLoading && route.path === activeRoute"
                            :width="2"
                            :size="18"
                            indeterminate />
                        <v-icon
                            v-else
                            :icon="route.icon"
                            size="18" />
                        <span
                            v-show="!rail"
                            class="truncate">{{ $t(route.label) }}</span>
                        <span
                            v-if="navBadge(route)"
                            v-show="!rail"
                            class="ml-auto text-[10px] font-bold rounded-full bg-secondary text-white px-1.5 py-0.5 leading-none min-w-[18px] text-center">{{ navBadge(route) }}</span>
                    </a>
                </nav>

                <div
                    v-if="activeDealership && pulse?.capacity"
                    v-show="!rail"
                    class="px-4 py-4 border-t border-white/10 shrink-0">
                    <div class="flex items-center justify-between mb-1.5">
                        <span class="text-[11px] font-semibold uppercase tracking-wider text-white/60">{{ $t('label.rack_capacity') }}</span>
                        <span class="font-mono text-[11px] text-white/90">{{ pulse.stored + ' / ' + pulse.capacity }}</span>
                    </div>
                    <div class="h-1.5 rounded-full bg-white/15 overflow-hidden">
                        <div
                            class="h-full rounded-full"
                            :class="capacityPercent >= 90 ? 'bg-warning' : 'bg-secondary'"
                            :style="{ width: capacityPercent + '%' }" />
                    </div>
                </div>

                <div
                    v-if="toolRoutes.length || showProjectSetup"
                    class="px-2.5 py-3 border-t border-white/10 space-y-0.5 shrink-0">
                    <a
                        v-for="route in toolRoutes"
                        :key="route.name"
                        v-tooltip.right="{ content: $t(route.label), disabled: !rail }"
                        class="relative flex items-center gap-3 py-2 rounded-md transition cursor-pointer text-white/70 hover:text-white hover:bg-white/10"
                        :class="rail ? 'justify-center px-0' : 'px-3'"
                        @click="visitPath(route)">
                        <v-icon
                            :icon="route.icon"
                            size="18" />
                        <span
                            v-show="!rail"
                            class="truncate">{{ $t(route.label) }}</span>
                    </a>
                    <a
                        v-if="showProjectSetup"
                        v-tooltip.right="{ content: $t('label.project_setup'), disabled: !rail }"
                        class="relative flex items-center gap-3 py-2 rounded-md transition cursor-pointer text-white/70 hover:text-white hover:bg-white/10"
                        :class="rail ? 'justify-center px-0' : 'px-3'"
                        @click="visitPath({ path: '/local/project', name: 'Local/ProjectSetup' })">
                        <v-icon
                            icon="mdi-wrench-outline"
                            size="18" />
                        <span
                            v-show="!rail"
                            class="truncate">{{ $t('label.project_setup') }}</span>
                    </a>
                </div>

                <div
                    v-tooltip.right="{ content: $t('label.profile'), disabled: !rail }"
                    class="flex items-center gap-3 px-4 py-3.5 border-t border-white/10 shrink-0 cursor-pointer"
                    :class="rail ? 'justify-center px-0' : ''"
                    @click="visitPath({ path: '/profile', name: 'Dashboard/UserProfilePage' })">
                    <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 bg-secondary">
                        {{ initials }}
                    </div>
                    <div
                        v-show="!rail"
                        class="min-w-0">
                        <p class="text-white text-xs font-semibold truncate">{{ profile?.first_name }} {{ profile?.last_name }}</p>
                        <p class="text-white/50 text-[11px] truncate">{{ roleLabel }}</p>
                    </div>
                    <button
                        v-show="!rail"
                        v-tooltip.right="$t('label.sign_out')"
                        class="ml-auto text-white/50 hover:text-white"
                        :aria-label="$t('label.sign_out')"
                        @click.stop.prevent="logout">
                        <v-icon
                            icon="mdi-logout"
                            size="16" />
                    </button>
                </div>

                <button
                    class="hidden md:flex items-center justify-center gap-2 py-2.5 border-t border-white/10 text-white/50 hover:text-white hover:bg-white/5 transition text-xs shrink-0 w-full"
                    @click="toggleRail">
                    <v-icon
                        icon="mdi-chevron-left"
                        size="15"
                        class="transition-transform"
                        :class="rail ? 'rotate-180' : ''" />
                    <span v-show="!rail">{{ $t('label.collapse') }}</span>
                </button>
            </div>
        </v-navigation-drawer>

        <v-app-bar
            v-if="!securityCheckpoint"
            :height="53"
            flat
            class="border-b border-line"
            color="surface"
            app>
            <div class="flex items-center gap-3 px-5 w-full">
                <button
                    class="md:hidden -ml-1 p-1.5 rounded-md hover:bg-black/5 text-ink-light"
                    :aria-label="$t('label.menu')"
                    @click="changeBar">
                    <v-icon
                        icon="mdi-menu"
                        size="20" />
                </button>
                <h1
                    v-if="pageTitle"
                    class="font-display font-semibold text-base text-ink">{{ $t(pageTitle) }}</h1>
                <div
                    id="app-bar-actions"
                    class="ml-auto flex items-center gap-3" />
                <template v-if="activeDealership">
                    <div class="relative hidden sm:block w-56 lg:w-72">
                        <v-icon
                            icon="mdi-magnify"
                            size="16"
                            class="absolute left-3 top-1/2 -translate-y-1/2 text-ink-light" />
                        <input
                            v-model="globalSearch"
                            type="text"
                            :placeholder="$t('label.global_search_placeholder')"
                            class="w-full rounded-lg border border-line bg-canvas pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-accent"
                            @keyup.enter="runGlobalSearch">
                    </div>
                    <button
                        class="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary-dark transition whitespace-nowrap"
                        @click="startCheckIn">
                        {{ '+ ' + $t('label.check_in') }}
                    </button>
                </template>
            </div>
        </v-app-bar>

        <v-main
            class="bg-canvas"
            app>
            <v-container
                fluid
                class="p-6">
                <slot />
            </v-container>
        </v-main>

        <div class="z-[1008]">
            <modals-container />
        </div>

        <v-footer
            class="bg-surface border-t border-line shrink-0"
            app>
            <div class="mb-0 flex justify-between w-full items-center mx-4 text-xs text-ink-light">
                <div>{{ $t('auth.last-login', { 'lastLogin': lastLogin }) }}</div>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <div>Copyright &copy; Fabled Solutions {{ new Date().getFullYear() }}</div>
                <v-btn-toggle
                    :model-value="locale"
                    class="!h-6 min-w-20 ml-2"
                    variant="text"
                    color="primary"
                    density="compact"
                    mandatory
                    group
                    @update:model-value="setLocale">
                    <v-btn
                        value="en"
                        size="small">{{ $t('common.en') }}</v-btn>
                    <v-btn
                        value="fr"
                        size="small">{{ $t('common.fr') }}</v-btn>
                </v-btn-toggle>
            </div>
        </v-footer>
    </v-app>
</div>
</template>
